import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
  MatOption,
} from '@angular/material/autocomplete';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import axios, { AxiosError } from 'axios';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { PayeeButtonComponent } from 'src/app/shared/payee-buttons/payee-button/payee-button.component';
import { ChipDropdownComponent } from 'src/app/shared/payee-cases/chip-dropdown/chip-dropdown.component';
import { PayeeCasesService } from 'src/app/shared/payee-cases/payee-cases.service';
import { DebtorOption } from 'src/app/shared/payee-cases/types';
import { PayeeFormFieldComponent } from 'src/app/shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from 'src/app/shared/payee-input/payee-input.directive';
import { environment } from 'src/environments/environment';
import { EventsService } from '../events.service';

@Component({
  selector: 'app-events-debtor-name-filter',
  templateUrl: './events-debtor-name-filter.component.html',
  imports: [
    FormsModule,
    ChipDropdownComponent,
    PayeeFormFieldComponent,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatProgressSpinner,
    MatAutocomplete,
    MatOption,
    PayeeButtonComponent,
    PayeeInputDirective,
    TranslateModule,
  ],
})
export class EventsDebtorNameFilterComponent implements OnInit, OnDestroy {
  private eventsService = inject(EventsService);
  private payeeCasesService = inject(PayeeCasesService);

  private readonly destroy = new Subject<void>();
  private readonly debtorSearch$ = new Subject<string>();
  readonly debtorNameControl = new FormControl<string | null>('', [
    Validators.required,
    Validators.minLength(3),
  ]);

  debtorOptions: DebtorOption[] = [];
  readonly loading = signal(false);

  dropdownOpen = false;

  get selected() {
    return this.eventsService.debtor_name.value;
  }

  ngOnInit(): void {
    this.eventsService.debtor_name
      .pipe(takeUntil(this.destroy))
      .subscribe((v) =>
        this.debtorNameControl.patchValue(v, { emitEvent: false }),
      );

    this.debtorSearch$
      .pipe(takeUntil(this.destroy), debounceTime(1000), distinctUntilChanged())
      .subscribe(async (name) => {
        if (name) {
          await this.setDebtors(name);
        }
      });

    this.payeeCasesService.client
      .pipe(takeUntil(this.destroy))
      .subscribe(() => (this.debtorOptions = []));
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.debtorSearch$.complete();
  }

  onDebtorSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.debtorSearch$.next(value);
  }

  onPaste(_event: ClipboardEvent): void {
    this.debtorNameControl.markAsTouched();
    setTimeout(() => {
      this.applyFilter();
    });
  }

  private async setDebtors(name?: string): Promise<void> {
    if (!this.payeeCasesService.getDebtorsPath.value) {
      console.warn('Get debtors path is not set!');
      return;
    }

    if (!this.dropdownOpen || !name || name.length < 3) {
      return;
    }

    try {
      this.loading.set(true);

      const response = await axios.get<{ debtors: DebtorOption[] }>(
        `${environment.baseUrl}/${this.payeeCasesService.getDebtorsPath.value}`,
        {
          params: {
            name,
            client: this.payeeCasesService.client.value?.uuid,
          },
        },
      );

      this.debtorOptions = response.data.debtors;
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error(
          'Network error while searching debtors:',
          error.response?.data,
        );
      } else {
        console.error('Unknown error searching debtors:', error);
      }
    } finally {
      this.loading.set(false);
    }
  }

  applyFilter(): void {
    if (this.debtorNameControl.invalid) {
      this.debtorNameControl.markAsDirty();
      return;
    }

    this.eventsService.debtor_name.next(this.debtorNameControl.value);
    this.dropdownOpen = false;
  }

  clearFilter(): void {
    this.eventsService.debtor_name.next(null);
    this.dropdownOpen = false;
  }

  displayWith(selected: string): string {
    return selected;
  }
}
